import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface AboutVideo {
  id: string;
  filename: string;
  video_url: string;
  video_url_mobile: string | null;
  display_order: number;
  is_active: boolean;
  duration_seconds: number | null;
  thumbnail_url: string | null;
  created_at: string;
  updated_at: string;
}

export function useAboutVideos() {
  return useQuery({
    queryKey: ["about_videos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("about_videos")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as AboutVideo[];
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

export function useActiveAboutVideos() {
  return useQuery({
    queryKey: ["about_videos", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("about_videos")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as AboutVideo[];
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

export function useAddAboutVideo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (video: Omit<AboutVideo, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("about_videos")
        .insert(video)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["about_videos"] });
      toast.success("Video added successfully");
    },
    onError: (error) => {
      toast.error("Failed to add video: " + error.message);
    },
  });
}

export function useUpdateAboutVideo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AboutVideo> & { id: string }) => {
      const { data, error } = await supabase
        .from("about_videos")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["about_videos"] });
      toast.success("Video updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update video: " + error.message);
    },
  });
}

export function useDeleteAboutVideo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("about_videos").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["about_videos"] });
      toast.success("Video deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete video: " + error.message);
    },
  });
}

export function useReorderAboutVideos() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (videos: { id: string; display_order: number }[]) => {
      const updates = videos.map(({ id, display_order }) =>
        supabase.from("about_videos").update({ display_order }).eq("id", id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw new Error("Failed to reorder some videos");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["about_videos"] });
    },
    onError: (error) => {
      toast.error("Failed to reorder videos: " + error.message);
    },
  });
}
